import { BaseInjectableComponent } from '@base/component/BaseInjectableComponent';
import { EnumCRUDActions, EnumPageDisplayModes, EnumPageEntitySources } from "@core/framework/sysenums.enum";
import { BaseEntity, BaseState, HistoryChangesSet, BaseEntitiesResponse } from "../entities/BaseEntities";
import { BaseEndpointCRUD } from "../endpoint/BaseEndpointCRUD";
import { BasePageComponent } from "./BasePageComponent";
import { ServiceBagService } from "./ServiceBag";
import { Observable, map, tap, Subscription } from 'rxjs';
import { SysGlobals } from '@m4u-angular/core/framework/sysglobals';
import { Utils } from "@core/helpers/Utils";
import { Component, ContentChildren, QueryList, ViewChildren } from '@angular/core';
import { FieldFormComponent } from '@m4u-angular/ui/layouts/fieldlayouts/field-form/fieldform.component';
import { DxCheckBoxComponent } from 'devextreme-angular';

@Component({
    selector: 'base-page-crud-component',
    template: '<div></div>'
})
export abstract class BasePageCRUDComponent<TEntity extends BaseEntity, TState extends BaseState, TEndpoint extends BaseEndpointCRUD<TEntity>> extends BasePageComponent<TEntity, TState>
{
    //Mode
    public IsAddMode: boolean;
    public IsSaving: boolean = false;
    private IsDuplicateMode: boolean;
    protected EntityEntryID: number;

    //Overrides
    protected OnEntitySaved() { }
    protected OnEntityLoaded() { }
    protected OnEntityDeleted() { }
    protected OnEntityNew() { }
    protected OnEntityValidate(): boolean { return true; }
    protected OnEntityInitObjectName(): string { return undefined; }
    protected OnEntityDuplicated() { }
    protected OnEntityPrepareForSave() { }
    protected OnEntityConfirmSave(resumeSave: () => void) { resumeSave(); }
    protected OnIsAllowDuplicate() { return false; }
    protected IsAllowNavbar() { return false; }

    // Functions validation the entity
    private EntityValidations: (() => Observable<boolean>)[] = [];
    private _subscriptions: Subscription[] = [];

    @ViewChildren(FieldFormComponent) private Fields_V: QueryList<FieldFormComponent>;
    @ContentChildren(FieldFormComponent) private Fields_C: QueryList<FieldFormComponent>;
    private get Fields(): FieldFormComponent[] { return Utils.CombineChildren(this.Fields_V, this.Fields_C) }

    constructor(
        A: ServiceBagService,
        PageName: string,
        EntityType: (new () => TEntity),
        StateType: (new () => TState),
        protected Endpoint: TEndpoint,
        DisplayMode: EnumPageDisplayModes = EnumPageDisplayModes.Window,
        EntityMode: EnumPageEntitySources = EnumPageEntitySources.CRUD,
    ) {
        super(A, PageName, EntityType, StateType, DisplayMode, EntityMode);
    }

    //This is done here since before this place, ViewChildren is not initiallized
    //DetectChanges is called in base class
    override OnInitComponentFinishedInternal() {
        if (this.IsAddMode) this.SetFieldDefaultValues();
        super.OnInitComponentFinishedInternal();
    }

    override OnInitComponentFinishedAfterDetectInternal() {
        super.OnInitComponentFinishedAfterDetectInternal();
        this.SetDefaultFieldsLabels();
    }

    //Actually initialize the component
    override OnInitComponentInternal() {
        this.IsDuplicateMode = this.QueryParams?.IsDuplicate == 'true';

        //Perform base initialization
        super.OnInitComponentInternal();

        //Read params from route snapshot
        var sParam: string = this.EntityEntryID || this.A.Navigator.GetRouteParams().EntryID;

        //Reset flags
        this.IsAddMode = false;
        this.IsLoading = true;

        //Fire Onnew or OnLoad event
        if (sParam == "new") {
            this.StartNewEntity();
        }
        else {
            if (+sParam > 0) {
                if (!this.EntityEntryID) this.EntityEntryID = +sParam;
                this.LoadEntity(+sParam);
            }
            else {
                this.UI.UnexpectedMessage("Invalid route was specified");
            }
        }

        if (this.OnIsAllowDuplicate() && !this.IsAddMode) {
            this.ContextMenuDefault.push({
                text: this.RC('titleDuplicate'),
                visible: true,
                onClick: this.OnDuplicateInternal.bind(this)
            })
        }

        this._subscriptions.push(this.A.SysEvents.Toolbar.subscribe(toolbarSettings => {
            if (!toolbarSettings || toolbarSettings.PageComponentName != this.ToolbarSettings.PageComponentName) return;

            this.SetDefaultFieldsLabels();
        }));
    }

    //Load an existing entity
    //InitEntity is called here as well since load is asynchronous event and we need 
    //the entity in existing state in order not to have undefined references in the template
    protected LoadEntity(iEntryID: number) {
        this.InitEntity();
        this.SetEntity(this.EntityType, this.Endpoint.Get(iEntryID), false, () => this.ReflectEntityLoaded());
    }

    //Perform entity validation
    protected ValidateEntity(): Promise<boolean> {
        if (!this.OnEntityValidate()) return new Promise<boolean>((response) => response(false));

        return new Promise<boolean>((resolve) => {
            const validations = this.AcceptGridChanges().concat(this.EntityValidations.map((x) => x().toPromise()));;

            //if no grids
            if (!validations.length) {
                resolve(true);
                return;
            }

            //wait for all validations
            Promise.all(validations).then((validationResults: []) => {
                //if all validations true
                if (validationResults.filter(r => r === false).length === 0)
                    resolve(true);
                else
                    resolve(false);
            });
        });
    }

    //Performs the life-cycle part after entity has been set to the component
    protected ReflectEntityLoaded() {
        this.IsAddMode = false;
        this.OnEntityLoaded();
        this.NotifyEntityLoaded(this.Entity, this.EntityType);
        this.NotifyReloadTitle();
        //this.RefreshEntityOriginal()
        this.IsLoading = false;
    }

    //Performs the life-cycle part after entity has been set to the component
    protected ReflectEntitySaved() {
        this.OnEntitySaved();
        this.NotifyEntitySaved(this.Entity, this.EntityType, this.IsAddMode ? EnumCRUDActions.Create : EnumCRUDActions.Update);
        this.IsAddMode = false;
        this.IsSaving = false;
        //this.RefreshEntityOriginal();
        this.NotifyReloadTitle();
    }

    //Performs the life-cycle part after entity has been set to the component
    protected ReflectEntityDeleted() {
        this.OnEntityDeleted();
        this.NotifyEntitySaved(this.Entity, this.EntityType, EnumCRUDActions.Delete);
        this.NavigateBack();
    }

    //Event handler for validating and saving current entity, DON'T override it
    public OnSave() {
        this.OnEntityPrepareForSave();
        this.ValidateEntity().then(isValid => {
            if (isValid) {
                this.OnEntityConfirmSave(() => this.OnDoSave());
            }
            else {
                this.IsDuplicateMode = false
            }
        });
    }

    //default save implementation, can be overriden
    protected OnDoSave() {
        this.IsSaving = true
        this.SetEntity(this.EntityType, this.Endpoint.Save(this.Entity), true, (_) => this.ReflectEntitySaved(), (response) => {
            this.A.UI.StopMessage(response.ResponseMessage);
            this.IsSaving = false;
        });
    }

    OnDelete() {
        this.DeleteEntity();
    }

    DeleteEntity() {
        this.Do(this.Endpoint.Delete(this.Entity.EntryID), true, () => this.ReflectEntityDeleted());
    }

    //Create a new entity
    protected StartNewEntity() {
        this.IsAddMode = true;
        this.OnEntityNew();
        this.RefreshEntityOriginal();
        this.IsLoading = false;
        this.SubscribeOnEntityChanges();
    }

    protected override InitEntity() {

        //Save global entity before it will be empty in base logic
        let duplicatedEntity = null;

        if (this.IsDuplicateMode) {
            let currentEntity = SysGlobals.CurrentEntity as TEntity;
            if (currentEntity) {
                duplicatedEntity = Utils.MapObject<TEntity>(this.EntityType, currentEntity);
                duplicatedEntity.EntryID = -1;
            }
        }

        super.InitEntity();

        // Duplicate entity to current
        if (this.IsDuplicateMode && duplicatedEntity) {
            this.Entity = Utils.MapObject<TEntity>(this.EntityType, duplicatedEntity);
            this.OnEntityDuplicated();

            this.IsDuplicateMode = false;
        }
    }

    public SetFieldDefaultValues() {
        this.A.SysEvents.OnSetDefaults.emit();
    }

    //Return title with document number
    protected override OnInitTitle(): string {
        var sObjectName: string = this.GetEntityObjectName();
        return `${this.GetDefaultTitle()}${(sObjectName ? (' - ' + sObjectName) : '')}`;
    }

    //Return entity object name allowing overide
    public GetEntityObjectName(): string {
        var sCustomObjectName: string = this.OnEntityInitObjectName();
        return sCustomObjectName ? sCustomObjectName : this.GetDefaultEntityObjectName();
    }

    public override OnInitInjectableComponent(injectableComponent: BaseInjectableComponent<BaseEntity, BaseState>) {
        // Set DocEntry in the injectable component
        injectableComponent.TargetDocEntry = this.EntityEntryID;
        // Get validation function for the entity from the injectable component
        this.EntityValidations.push(injectableComponent.IsValidEntity);
    }

    //Return default name for the object
    private GetDefaultEntityObjectName(): string {
        return (this.Entity != null && this.Entity.EntryID > 0 ? `${this.Entity.EntryID}` : '')
    }

    OnDuplicateInternal() {
        const currentRoute = this.A.Navigator.GetCurrentRouteWithoutParams();
        this.A.Navigator.Navigate([`/pages/${currentRoute}/new`], { IsDuplicate: true });
    }

    override OnGetHistoryInternal() {
        return this.Endpoint.GetHistory(this.Entity.EntryID)
            .pipe(
                tap(h => { if (!h.IsSuccess) { this.UI.StopMessage(h.ResponseMessage); } }),
                map(h => h.Entities)
            );
    }

    SetDefaultFieldsLabels() {
        setTimeout(() => this.Fields.forEach(field => {
            if (Utils.IsEmpty(this.Entity.TypeName)) return;

            const control = field.GetEditorControl();
            const fieldLabel = this.RC(`BL.${this.Entity.TypeName}.${field.Name}`);

            if (control instanceof DxCheckBoxComponent) {
                Utils.IsEmpty(control.text) && Utils.IsEmpty(field.Label) && (control.text = fieldLabel);
            } else {
                Utils.IsEmpty(field.Label) && (field.Label = fieldLabel);
            }
        }));
    }

    override OnDestroyComponentInternal() {
        super.OnDestroyComponentInternal();
        this._subscriptions.forEach(x => x.unsubscribe());
        this._subscriptions = [];
    }
}
